'use client';

import { c as _c } from "react/compiler-runtime";
import { buttonVariants } from '@/components/ui/buttons/button';
import { Card } from '@/components/ui/card';
import { LinkComponent } from '@/components/ui/link-component';
import { SectionLabel } from '@/components/ui/section-label';
import { DocumentDownloadRow } from '@/features/payload-cms/components/content-blocks/file-download';
import { HighlightedText } from '@/features/payload-cms/components/search/highlighted-text';
import { resultMetaParts } from '@/features/payload-cms/components/search/search-result-meta';
import { fileUrlOnPage } from '@/features/payload-cms/components/search/search-result-row';
import { closePreviewLabel, linkedFromLabel, matchCountLabel, matchesLabel, openLabel, pageCountLabel, pageShortLabel, shownOnLabel, typeLabelSingular } from '@/features/payload-cms/components/search/search-translations';
import type { SearchDocumentDetails, SearchLinkedPage, SearchResult } from '@/features/payload-cms/search/search-types';
import type { Locale } from '@/types/types';
import { X } from 'lucide-react';
import React from 'react';
const pageChipClassName = 'bg-chip text-heading shrink-0 rounded-[5px] px-1.5 py-0.5 text-[11px] font-bold';
const PageLinks: React.FC<{
  label: string;
  pages: SearchLinkedPage[];
}> = t0 => {
  const $ = _c(5);
  const {
    label,
    pages
  } = t0;
  let t1;
  if ($[0] !== pages) {
    t1 = pages.map(_temp);
    $[0] = pages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <p className="border-line mt-5 border-t pt-4 text-[13px] text-green-700">{label}{" "}{t1}</p>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/**
 * The document as every download list shows it, then where the words stand in it, each Fundstelle
 * a link to its page.
 */
const DocumentPreview: React.FC<{
  result: SearchResult;
  details: SearchDocumentDetails;
  words: string[];
  locale: Locale;
}> = t0 => {
  const $ = _c(22);
  const {
    result,
    details,
    words,
    locale
  } = t0;
  let t1;
  if ($[0] !== details.fileUrl || $[1] !== details.filename || $[2] !== details.filesize || $[3] !== details.mimeType || $[4] !== details.thumbnailUrl || $[5] !== details.updatedAt || $[6] !== locale || $[7] !== result.title) {
    t1 = <Card as="ul"><DocumentDownloadRow href={details.fileUrl} displayName={result.title} filename={details.filename} mimeType={details.mimeType} thumbnailUrl={details.thumbnailUrl} filesize={details.filesize} updatedAt={details.updatedAt} openInNewTab={true} locale={locale} /></Card>;
    $[0] = details.fileUrl;
    $[1] = details.filename;
    $[2] = details.filesize;
    $[3] = details.mimeType;
    $[4] = details.thumbnailUrl;
    $[5] = details.updatedAt;
    $[6] = locale;
    $[7] = result.title;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== details.fileUrl || $[10] !== details.isPdf || $[11] !== details.matches || $[12] !== locale || $[13] !== words) {
    t2 = details.matches.length > 0 && <><SectionLabel as="h3" className="mt-5">{matchesLabel[locale]} · {details.matches.length}</SectionLabel><ul className="mt-1 flex flex-col">{details.matches.map(match => <li key={match.page}><a href={details.isPdf ? fileUrlOnPage(details.fileUrl, match.page) : details.fileUrl} target="_blank" rel="noopener" className="hover:bg-quiet flex min-h-11 items-start gap-2.5 rounded-lg px-2 py-2 text-[14px] text-green-700"><span className={pageChipClassName}>{pageShortLabel(match.page)[locale]}</span><span><HighlightedText text={match.snippet} words={words} /></span></a></li>)}</ul></>;
    $[9] = details.fileUrl;
    $[10] = details.isPdf;
    $[11] = details.matches;
    $[12] = locale;
    $[13] = words;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== details.linkedFrom || $[16] !== locale) {
    t3 = details.linkedFrom.length > 0 && <PageLinks label={linkedFromLabel[locale]} pages={details.linkedFrom} />;
    $[15] = details.linkedFrom;
    $[16] = locale;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};

/**
 * Everything about one result beside the list: for a PDF its pages, where the words stand on
 * them, and the pages that link to it; for a page or a news entry its passage and the way there.
 */
export const SearchPreview: React.FC<{
  result: SearchResult;
  words: string[];
  locale: Locale;
  /** given where the preview can be closed to widen the list */
  onClose?: () => void;
  className?: string;
}> = t0 => {
  const $ = _c(35);
  const {
    result,
    words,
    locale,
    onClose,
    className
  } = t0;
  const details = result.document;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== className || $[1] !== details || $[2] !== locale || $[3] !== result) {
    const meta = details === undefined ? resultMetaParts(result, locale) : [details.pageCount === undefined ? undefined : pageCountLabel(details.pageCount)[locale], details.matches.length === 0 ? undefined : matchCountLabel(details.matches.length)[locale]].filter(_temp2);
    const label = details?.typeLabel ?? typeLabelSingular[result.type][locale];
    t5 = className;
    t4 = "flex items-start gap-3";
    t1 = "flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-green-500";
    if ($[9] !== label) {
      t2 = <span className="bg-chip text-heading rounded-[5px] px-1.5 py-0.5 text-[11px] font-bold tracking-wide uppercase">{label}</span>;
      $[9] = label;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t3 = result.type === "page" ? undefined : meta.join(" \xB7 ");
    $[0] = className;
    $[1] = details;
    $[2] = locale;
    $[3] = result;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t6 = <p className={t1}>{t2}{t3}</p>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== locale || $[16] !== onClose) {
    t7 = onClose !== undefined && <button type="button" onClick={onClose} aria-label={closePreviewLabel[locale]} className={buttonVariants({
      variant: "quiet",
      size: "touchIcon"
    })}><X aria-hidden="true" /></button>;
    $[15] = locale;
    $[16] = onClose;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t6 || $[20] !== t7) {
    t8 = <div className={t4}>{t6}{t7}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== result.title || $[23] !== words) {
    t9 = <h2 className="font-heading text-heading mt-2 mb-4 text-[24px] leading-tight font-extrabold"><HighlightedText text={result.title} words={words} /></h2>;
    $[22] = result.title;
    $[23] = words;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== details || $[26] !== locale || $[27] !== result || $[28] !== words) {
    t10 = details === undefined ? <>{result.excerpt !== "" && <p className="text-[15px] leading-relaxed text-green-700"><HighlightedText text={result.excerpt} words={words} /></p>}<div className="mt-4"><LinkComponent href={result.url} prefetch={true} className={buttonVariants({
          variant: "primary",
          size: "touch"
        })}>{openLabel[locale]}</LinkComponent></div>{result.shownOn !== undefined && <PageLinks label={shownOnLabel[locale]} pages={[result.shownOn]} />}</> : <DocumentPreview result={result} details={details} words={words} locale={locale} />;
    $[25] = details;
    $[26] = locale;
    $[27] = result;
    $[28] = words;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t5 || $[32] !== t8 || $[33] !== t9) {
    t11 = <article className={t5}>{t8}{t9}{t10}</article>;
    $[30] = t10;
    $[31] = t5;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};
function _temp(page, index) {
  return <React.Fragment key={page.url}>{index > 0 && ", "}<LinkComponent href={page.url} className="text-conveniat-green font-semibold underline">{page.title}</LinkComponent></React.Fragment>;
}
function _temp2(part) {
  return part !== undefined;
}